import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  CardActionArea,
  Typography,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import PlaceIcon from "@mui/icons-material/Place";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import SpaIcon from "@mui/icons-material/Spa";
import { getAuth } from "firebase/auth";
import IconMessageWhite from "../assets/icons/message-white.svg";
import IconMarkerPublic from "../assets/icons/v2/map-pin-public.webp";
import IconSeededPublicCapsule from "../assets/icons/v2/map-pin-seeded.webp";
import IconDraftCapsule from "../assets/icons/v2/time-capsule-draft.webp";
import IconSentCapsule from "../assets/icons/v2/time-capsule-sent.webp";
import IconInsiderStar from "../assets/icons/v2/insider-star.webp";
import { buildDiscoveryRowLabels } from "../utils/discoveryRowLabels";
import { BOTTOM_NAV_BAR_HEIGHT, TOP_APP_BAR_HEIGHT } from "../constants/constants";
import {
  getCapsuleDisplayLabel,
  getCapsuleDisplayKind,
} from "../utils/capsuleDisplay";

const SEED_TYPE_ICON_BY_KEY = {
  see: PlaceIcon,
  eat: RestaurantIcon,
  drink: LocalBarIcon,
  fun: SportsEsportsIcon,
  do: SportsEsportsIcon,
  shop: ShoppingBagIcon,
  relax: SpaIcon,
};
const SEED_TYPE_LABEL_BY_KEY = {
  see: "Place",
  eat: "Eat",
  drink: "Drink",
  fun: "Fun",
  do: "Fun",
  shop: "Shop",
  relax: "Relax",
};
const QUICK_FIND_TITLE_BY_CATEGORY = {
  eat: "Places to Eat We Found for You",
  drink: "Drink Spots We Found for You",
  fun: "Fun Finds We Found for You",
};
const GENERIC_DISCOVER_TITLE = "Fresh Finds We Found for You";
const MARKER_SELECT_SETTLE_MS = 350;
const SHOW_CLUSTER_SHORTCUT_CARDS = false;
const SHOW_CLUSTER_CATEGORY_FILTERS = false;

function getDraftPreviewSrc(draft) {
  const mediaItems = [
    ...(Array.isArray(draft?.media) ? draft.media : []),
    ...(Array.isArray(draft?.mediaFiles) ? draft.mediaFiles : []),
  ];
  const imageItem = mediaItems.find((item) => {
    const type = String(item?.type || item?.contentType || "").trim().toLowerCase();
    if (type && !type.startsWith("image")) return false;
    return Boolean(
      item?.url ||
      item?.photoUrl ||
      item?.downloadURL ||
      item?.previewUrl ||
      item?.sessionPreviewUrl ||
      item?.thumbnailUrl
    );
  });

  return String(
    imageItem?.url ||
    imageItem?.photoUrl ||
    imageItem?.downloadURL ||
    imageItem?.previewUrl ||
    imageItem?.sessionPreviewUrl ||
    imageItem?.thumbnailUrl ||
    ""
  ).trim();
}

function normalizeUiState(uiState = {}) {
  const normalizedViewMode =
    uiState?.viewMode === "drafts" || uiState?.viewMode === "insiders"
      ? uiState.viewMode
      : "discover";
  return {
    viewMode: normalizedViewMode,
    scrollTop: Math.max(0, Math.round(Number(uiState?.scrollTop || 0))),
  };
}

function areUiStatesEquivalent(a, b) {
  const left = normalizeUiState(a);
  const right = normalizeUiState(b);
  return (
    left.viewMode === right.viewMode &&
    left.scrollTop === right.scrollTop
  );
}

function getDiscoverResultsTitle(markers = []) {
  const categoryKeys = new Set(
    (Array.isArray(markers) ? markers : [])
      .map((marker) => String(marker?.quickFindCategoryKey || "").trim().toLowerCase())
      .filter((key) => Boolean(QUICK_FIND_TITLE_BY_CATEGORY[key]))
  );

  if (categoryKeys.size === 1) {
    return QUICK_FIND_TITLE_BY_CATEGORY[Array.from(categoryKeys)[0]];
  }

  return GENERIC_DISCOVER_TITLE;
}

export default function CapsuleClusterModal({
  open,
  markers = [],
  drafts = [],
  mode = "discover",
  onSelect,
  onSelectDraft,
  onClose,
  onBury,
  onOpenInsiders,
  onRefreshUncover,
  isInsider = false,
  answerText = "",
  locationLabel = "",
  visibilityByKey = {},
  onToggleVisibility,
  initialUiState = null,
  onUiStateChange,
  bottomReservedSpace = "0px",
  emptyMessage,  // admin-configured "no capsules found" message
}) {
  const isLocationClusterMode = mode === "cluster";
  const currentUserId = getAuth()?.currentUser?.uid || "";
  const isFindResultsView = String(answerText || "").trim().length > 0;
  const findLocationLabel = String(locationLabel || "").trim();
  const isDiscoverResultsView = mode === "discover";
  const discoverResultsTitle = useMemo(
    () => getDiscoverResultsTitle(markers),
    [markers]
  );
  const suppressCategoryControls = isFindResultsView || isDiscoverResultsView;
  const categories = useMemo(
    () => [
      {
        key: "received",
        label: "Received",
        filter: (m) => getCapsuleDisplayKind(m, currentUserId) === "received",
      },
      {
        key: "buried",
        label: "Buried",
        filter: (m) => getCapsuleDisplayKind(m, currentUserId) === "sent",
      },
      {
        key: "personal",
        label: "Memories",
        filter: (m) => getCapsuleDisplayKind(m, currentUserId) === "memory",
      },
      {
        key: "public",
        label: "Public",
        filter: (m) => {
          const kind = getCapsuleDisplayKind(m, currentUserId);
          return kind === "public" || kind === "featured" || kind === "exclusive";
        },
      },
    ],
    [currentUserId]
  );

  const [viewMode, setViewMode] = useState("discover");
  const [failedPreviewIds, setFailedPreviewIds] = useState(() => new Set());
  const contentRef = useRef(null);
  const openedAtRef = useRef(0);
  const initializedFromPropRef = useRef(false);
  const restoredInitialScrollRef = useRef(false);
  const lastEmittedUiStateRef = useRef(null);

  useEffect(() => {
    if (!open) {
      openedAtRef.current = 0;
      initializedFromPropRef.current = false;
      restoredInitialScrollRef.current = false;
      lastEmittedUiStateRef.current = null;
      setFailedPreviewIds(new Set());
      return;
    }
    openedAtRef.current = Date.now();
    if (initializedFromPropRef.current) return;
    initializedFromPropRef.current = true;
    const normalized = normalizeUiState(initialUiState);
    setViewMode(normalized.viewMode);
  }, [open, initialUiState]);

  useEffect(() => {
    if (!open) return;
    if (restoredInitialScrollRef.current) return;
    const top = Number(initialUiState?.scrollTop);
    if (!Number.isFinite(top) || top <= 0) {
      restoredInitialScrollRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = top;
      }
      restoredInitialScrollRef.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, [open, initialUiState, markers.length, drafts.length]);

  const emitUiState = useCallback(
    (override = {}) => {
      if (!onUiStateChange) return;
      const nextUiState = normalizeUiState({
        viewMode,
        scrollTop: Number(contentRef.current?.scrollTop || 0),
        ...override,
      });
      if (areUiStatesEquivalent(lastEmittedUiStateRef.current, nextUiState)) return;
      lastEmittedUiStateRef.current = nextUiState;
      onUiStateChange(nextUiState);
    },
    [onUiStateChange, viewMode]
  );

  useEffect(() => {
    if (!open) return;
    emitUiState();
  }, [emitUiState, open]);

  const handleContentScroll = useCallback(() => {
    emitUiState();
  }, [emitUiState]);

  const canSelectMarkerRow = useCallback(() => {
    const openedAt = Number(openedAtRef.current || 0);
    return !openedAt || Date.now() - openedAt >= MARKER_SELECT_SETTLE_MS;
  }, []);

  // The map-marker tap that opens this modal keeps propagating: its click/
  // pointerup lands on the freshly-mounted Dialog backdrop and would fire an
  // immediate `backdropClick` close, so the modal appears not to open at all
  // (worse on small 2-item clusters; rapid taps sometimes win the race).
  // Swallow a backdrop close that arrives within the same settle window we
  // already use to gate row selection. The X button, Escape, and later
  // backdrop clicks are unaffected.
  const handleDialogClose = useCallback(
    (event, reason) => {
      if (reason === "backdropClick" && !canSelectMarkerRow()) return;
      onClose?.(event, reason);
    },
    [canSelectMarkerRow, onClose]
  );

  const getMarkerCategoryKey = useCallback((marker) => {
    const matched = categories.find((cat) => cat.filter(marker));
    return matched?.key || "";
  }, [categories]);

  const isCategoryVisible = useCallback((key) => {
    if (!SHOW_CLUSTER_CATEGORY_FILTERS) return true;
    if (!key) return true;
    return visibilityByKey[key] !== false;
  }, [visibilityByKey]);

  const isInsidersView = viewMode === "insiders";
  const visibleMarkers = useMemo(() => {
    const list = Array.isArray(markers) ? markers : [];
    if (isFindResultsView) {
      return list.filter(
      (marker) => String(marker?.capsuleType || "").trim().toLowerCase() !== "sponsored"
      );
    }
    return list.filter((marker) => isCategoryVisible(getMarkerCategoryKey(marker)));
  }, [getMarkerCategoryKey, isCategoryVisible, isFindResultsView, markers]);
  const visibleInsiderMarkers = useMemo(
    () => visibleMarkers.filter((marker) => marker?.insider === true),
    [visibleMarkers]
  );
  const visibleDrafts = useMemo(
    () => drafts,
    [drafts]
  );

  const filteredMarkers = useMemo(() => {
    return isInsidersView ? visibleInsiderMarkers : visibleMarkers;
  }, [isInsidersView, visibleInsiderMarkers, visibleMarkers]);
  const orderedFilteredMarkers = useMemo(() => {
    const rankForMarker = (marker) => {
      if (marker?.insider === true) return 0;
      const capsuleType = String(marker?.capsuleType || "").trim().toLowerCase();
      if (capsuleType === "sponsored") return 1;
      if (capsuleType === "memberexclusive") return 2;
      if (capsuleType === "private") return 3;
      if (capsuleType === "public") return 4;
      return 5;
    };

    return filteredMarkers
      .map((marker, idx) => ({ marker, idx }))
      .sort((a, b) => {
        const rankDiff = rankForMarker(a.marker) - rankForMarker(b.marker);
        if (rankDiff !== 0) return rankDiff;
        return a.idx - b.idx;
      })
      .map((entry) => entry.marker);
  }, [filteredMarkers]);
  const hasUnderlyingMarkers = isInsidersView ? visibleInsiderMarkers.length > 0 : markers.length > 0;
  const hasUnderlyingDrafts = !isInsidersView && drafts.length > 0;
  const hasAnyMarkers = isInsidersView ? visibleInsiderMarkers.length > 0 : visibleMarkers.length > 0;
  const hasDrafts = !isInsidersView && visibleDrafts.length > 0;
  const hasAnyResults = hasAnyMarkers || hasDrafts;
  const hasUnderlyingResults = hasUnderlyingMarkers || hasUnderlyingDrafts;
  const isHiddenByFilters = !suppressCategoryControls && hasUnderlyingResults && !hasAnyResults;
  const countsByKey = useMemo(() => {
    const counts = {};
    categories.forEach((cat) => {
      counts[cat.key] = markers.filter(cat.filter).length;
    });
    return counts;
  }, [categories, markers]);
  const insidersCount = useMemo(
    () =>
      markers.filter((m) => m?.insider === true).length,
    [markers]
  );
  const hasInsiders = insidersCount > 0;
  const showBuryCta =
    !suppressCategoryControls &&
    (
      visibilityByKey.received !== false ||
      visibilityByKey.buried !== false ||
      visibilityByKey.personal !== false
    );

  const formatDraftDate = useCallback((draft) => {
    const raw =
      draft?.lastEditedAt?.toDate?.() ||
      draft?.createdAt?.toDate?.() ||
      (draft?.lastEditedAt
        ? new Date(draft.lastEditedAt)
        : (draft?.createdAt ? new Date(draft.createdAt) : null));

    if (!(raw instanceof Date) || Number.isNaN(raw.getTime())) {
      return "Saved draft";
    }

    return `Last edited ${raw.toLocaleDateString()}`;
  }, []);

  const renderList = () => (
    <Box sx={{ display: "grid", gap: 1 }}>
      {orderedFilteredMarkers.map((m) => (
        (() => {
          const seedTypeKey = String(m?.seedType || "").trim().toLowerCase();
          const quickFindCategoryKey = String(m?.quickFindCategoryKey || "").trim().toLowerCase();
          const fallbackTypeKey = quickFindCategoryKey || seedTypeKey;
          const shouldShowSeedTypeIcon = m?.seeded === true && Boolean(SEED_TYPE_ICON_BY_KEY[seedTypeKey]);
          const SeedTypeIcon = shouldShowSeedTypeIcon ? SEED_TYPE_ICON_BY_KEY[seedTypeKey] : null;
          const FallbackTypeIcon = SEED_TYPE_ICON_BY_KEY[fallbackTypeKey] || SeedTypeIcon || PlaceIcon;
          const rowLabels = buildDiscoveryRowLabels(
            {
              capsuleType: m?.capsuleType,
              title: m?.title,
              companyName: m?.companyName,
              senderLabel: m?.senderLabel,
              locationName: m?.placeName,
              address: m?.address,
            },
            { isInsider }
          );
          const fallbackTypeLabel = SEED_TYPE_LABEL_BY_KEY[fallbackTypeKey] || "Time capsule";
          const isBrandedPublicCapsule = m?.icon === IconMarkerPublic;
          const displayKind = getCapsuleDisplayKind(m, currentUserId);
          const isBuriedItem = m?.isSent === true || displayKind === "sent";
          const previewSrc = isBuriedItem
            ? IconSentCapsule
            : m?.heroUrl || (isBrandedPublicCapsule ? IconSeededPublicCapsule : m.icon);
          const markerId = String(m?.id || "");
          const previewFailed = markerId && failedPreviewIds.has(markerId);
          const isInsiderItem = m?.insider === true;
          return (
        <Box
          key={m.id}
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            overflow: "hidden",
          })}
        >
          <CardActionArea
            onClick={() => {
              if (!canSelectMarkerRow()) return;
              onSelect(m, {
                fromClusterModal: true,
                uiState: {
                  viewMode,
                  scrollTop: Number(contentRef.current?.scrollTop || 0),
                },
              });
            }}
            sx={{ px: 1.25, py: 1.15 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.35 }}>
              <Box
                sx={{
                  position: "relative",
                  width: 76,
                  height: 76,
                  flexShrink: 0,
                }}
              >
                {!previewFailed ? (
                  <Box
                    component="img"
                    src={previewSrc}
                    alt="Capsule"
                    onError={() => {
                      if (!markerId) return;
                      setFailedPreviewIds((prev) => {
                        if (prev.has(markerId)) return prev;
                        const next = new Set(prev);
                        next.add(markerId);
                        return next;
                      });
                    }}
                    sx={{
                      width: isBuriedItem ? "60%" : 76,
                      height: isBuriedItem ? "60%" : 76,
                      objectFit: isBuriedItem ? "contain" : "cover",
                      borderRadius: 0.5,
                      display: "block",
                      mx: isBuriedItem ? "auto" : 0,
                      my: isBuriedItem ? "20%" : 0,
                    }}
                  />
                ) : (
                  <Box
                    data-testid={`capsule-row-fallback-icon-${fallbackTypeKey || "capsule"}`}
                    aria-label={`${fallbackTypeLabel} capsule image fallback`}
                    sx={(theme) => ({
                      width: 76,
                      height: 76,
                      borderRadius: 0.5,
                      display: "grid",
                      placeItems: "center",
                      color: theme.colors?.white || theme.palette.common.white,
                      backgroundColor:
                        quickFindCategoryKey === "eat"
                          ? "#F26B3A"
                          : quickFindCategoryKey === "drink"
                            ? "#2CA6B5"
                            : quickFindCategoryKey === "fun"
                              ? "#5B4BFF"
                              : alpha(theme.palette.primary.main, 0.22),
                    })}
                  >
                    <FallbackTypeIcon sx={{ fontSize: 34 }} />
                  </Box>
                )}
                {isInsiderItem ? (
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      top: -4,
                      right: -4,
                      width: 16,
                      height: 16,
                      borderRadius: "999px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: theme.palette.warning.main,
                      color: theme.palette.common.white,
                      boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                    })}
                  >
                    <StarIcon sx={{ fontSize: 11 }} />
                  </Box>
                ) : null}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  {SeedTypeIcon ? (
                    <Box
                      sx={(theme) => ({
                        width: 20,
                        height: 20,
                        borderRadius: "999px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.colors.green,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(86,195,114,0.22)"
                            : "rgba(86,195,114,0.14)",
                        flexShrink: 0,
                      })}
                    >
                      <SeedTypeIcon sx={{ fontSize: 13 }} />
                    </Box>
                  ) : null}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      aria-label={`Capsule type: ${getCapsuleDisplayLabel(m, currentUserId)}`}
                      sx={(theme) => ({
                        display: "inline-block",
                        verticalAlign: "middle",
                        px: 0.8,
                        py: 0.2,
                        mr: 0.6,
                        borderRadius: "999px",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: theme.palette.text.secondary,
                        backgroundColor: theme.palette.action.selected,
                        whiteSpace: "nowrap",
                      })}
                    >
                      {getCapsuleDisplayLabel(m, currentUserId)}
                    </Box>
                    {rowLabels.title}
                  </Typography>
                </Box>
                {rowLabels.secondary ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      mt: 0.35,
                    }}
                  >
                    {rowLabels.secondary}
                  </Typography>
                ) : null}
                {rowLabels.tertiary ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      opacity: 0.8,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {rowLabels.tertiary}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </CardActionArea>
        </Box>
          );
        })()
      ))}
    </Box>
  );

  const renderDraftList = () => (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Button
        onClick={() => {
          setViewMode("discover");
          emitUiState({ viewMode: "discover" });
        }}
        variant="text"
        sx={(theme) => ({
          justifyContent: "flex-start",
          textTransform: "none",
          px: 0.25,
          minHeight: 28,
          width: "fit-content",
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.primary.main,
          opacity: theme.palette.mode === "dark" ? 0.92 : 1,
        })}
      >
        Back to Discover
      </Button>

      {visibleDrafts.map((draft) => {
        const draftPreviewSrc = getDraftPreviewSrc(draft);
        const previewSrc = draftPreviewSrc || IconDraftCapsule;
        return (
        <Box
          key={draft.id}
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            overflow: "hidden",
          })}
        >
          <CardActionArea
            onClick={() => {
              if (onSelectDraft) onSelectDraft(draft);
            }}
            sx={{ px: 1.25, py: 1.1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                component="img"
                src={previewSrc}
                alt={`${draft.messageTitle || draft.location?.placeName || "Draft"} preview`}
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: 0.5,
                  objectFit: draftPreviewSrc ? "cover" : "contain",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {draft.messageTitle || draft.location?.placeName || "Untitled draft"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {draft.address || draft.location?.address || "No location details"}
                </Typography>
              </Box>
              <Box
                sx={(theme) => ({
                  px: 0.8,
                  py: 0.2,
                  borderRadius: "999px",
                  fontSize: "0.68rem",
                  lineHeight: 1.2,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.action.selected,
                  flexShrink: 0,
                })}
              >
                Draft
              </Box>
            </Box>

            <Typography
              variant="caption"
              sx={(theme) => ({
                mt: 0.75,
                display: "block",
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[300]
                    : theme.palette.text.secondary,
              })}
            >
              {formatDraftDate(draft)}
            </Typography>
          </CardActionArea>
        </Box>
        );
      })}
    </Box>
  );

  const renderDiscoverView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ flexShrink: 0 }}>
      {SHOW_CLUSTER_SHORTCUT_CARDS && !isInsidersView && (hasInsiders || hasDrafts) && (
      <Grid container spacing={1} sx={{ mb: 1.25 }} alignItems="stretch">
        {hasInsiders && (
        <Grid item xs={hasDrafts ? 6 : 12} sx={{ display: "flex" }}>
          <Box
            sx={(theme) => ({
              width: "100%",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              overflow: "hidden",
            })}
          >
              <CardActionArea
                onClick={() => {
                  setViewMode("insiders");
                  emitUiState({ viewMode: "insiders" });
                  if (onOpenInsiders) onOpenInsiders();
                }}
                sx={{ px: 1.25, py: 1, minHeight: 92, height: "100%" }}
              >
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    component="img"
                    src={IconInsiderStar}
                    alt="Insider star"
                    sx={{
                      width: 46,
                      height: 46,
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Insiders
                      </Typography>
                      <Box
                        sx={(theme) => ({
                          px: 0.65,
                          py: 0.15,
                          borderRadius: "999px",
                          fontSize: "0.68rem",
                          lineHeight: 1.2,
                          color: theme.palette.text.secondary,
                          backgroundColor: theme.palette.action.selected,
                          flexShrink: 0,
                        })}
                      >
                        {insidersCount}
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Curated capsules
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardActionArea>
          </Box>
        </Grid>
        )}

        {hasDrafts && (
          <Grid item xs={hasInsiders ? 6 : 12} sx={{ display: "flex" }}>
            <Box
              sx={(theme) => ({
                width: "100%",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.38)}`,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.045),
                boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.16)}`,
              })}
            >
              <CardActionArea
                aria-label="Open drafts"
                onClick={() => {
                  setViewMode("drafts");
                  emitUiState({ viewMode: "drafts" });
                }}
                sx={(theme) => ({
                  px: 1.25,
                  py: 1,
                  minHeight: 92,
                  height: "100%",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                })}
              >
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box
                      component="img"
                      src={IconDraftCapsule}
                      alt="Draft time capsule"
                      sx={{
                        width: 46,
                        height: 46,
                        objectFit: "contain",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Drafts
                        </Typography>
                        <Box
                          sx={(theme) => ({
                            px: 0.65,
                            py: 0.15,
                            borderRadius: "999px",
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.action.selected,
                            flexShrink: 0,
                          })}
                        >
                          {visibleDrafts.length}
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Keep going
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardActionArea>
            </Box>
          </Grid>
        )}
      </Grid>
      )}

      {SHOW_CLUSTER_CATEGORY_FILTERS && !suppressCategoryControls && !isInsidersView && hasUnderlyingMarkers && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {categories.map((cat) => {
            const selected = visibilityByKey[cat.key] !== false;
            const isVisible = visibilityByKey[cat.key] !== false;
            return (
              <Grid item xs={6} key={cat.key}>
                <Button
                  fullWidth
                  onClick={() => {
                    if (onToggleVisibility) onToggleVisibility(cat.key);
                  }}
                  variant="outlined"
                  sx={(theme) => ({
                    textTransform: "none",
                    justifyContent: "space-between",
                    borderRadius: 2,
                    py: 1.2,
                    px: 1.25,
                    borderColor: selected ? theme.palette.success.main : theme.palette.divider,
                    color: selected ? theme.palette.success.main : theme.palette.text.primary,
                    backgroundColor: selected
                      ? theme.palette.mode === "dark"
                        ? alpha(theme.palette.success.main, 0.15)
                        : alpha(theme.palette.success.main, 0.08)
                      : "transparent",
                    "&:hover": {
                      borderColor: selected ? theme.palette.success.main : theme.palette.primary.main,
                      backgroundColor: selected
                        ? theme.palette.mode === "dark"
                          ? alpha(theme.palette.success.main, 0.2)
                          : alpha(theme.palette.success.main, 0.12)
                        : theme.palette.action.hover,
                    },
                  })}
                >
                  <Box component="span">{cat.label}</Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.4,
                      ml: 1,
                      minWidth: 54,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={(theme) => ({
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        borderRadius: "999px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.72rem",
                        lineHeight: 1,
                        backgroundColor: selected
                          ? theme.palette.success.main
                          : theme.palette.action.selected,
                        color: selected
                          ? theme.palette.success.contrastText
                          : theme.palette.text.secondary,
                      })}
                    >
                      {countsByKey[cat.key] || 0}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        minWidth: 12,
                        fontSize: "0.72rem",
                        color: isVisible ? "success.main" : "text.disabled",
                      }}
                    >
                      {isVisible ? "On" : "Off"}
                    </Box>
                  </Box>
                </Button>
              </Grid>
            );
          })}
        </Grid>
      )}

      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", mr: -2, pr: 2, pb: 0.5 }}>
        {isInsidersView && (
          <Button
            onClick={() => {
              setViewMode("discover");
              emitUiState({ viewMode: "discover" });
            }}
            variant="text"
            sx={(theme) => ({
              justifyContent: "flex-start",
              textTransform: "none",
              px: 0.25,
              minHeight: 28,
              width: "fit-content",
              mb: 1,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : theme.palette.primary.main,
              opacity: theme.palette.mode === "dark" ? 0.92 : 1,
            })}
          >
            Back to Discover
          </Button>
        )}
        {orderedFilteredMarkers.length === 0 ? (
          <Box sx={{ textAlign: "center", pt: 2, pb: 0.75 }}>
          {!isHiddenByFilters && (
            <Typography variant="body2" sx={{ fontSize: "1rem", mb: 2 }}>
              {isInsidersView
                ? "No Inssa picks nearby yet."
                : isFindResultsView
                ? "No matching time capsules are ready to show yet."
                : hasAnyResults
                ? "No time capsules buried here yet."
                : emptyMessage && emptyMessage.trim().length > 0
                  ? emptyMessage
                  : showBuryCta
                    ? `You’ve discovered an empty spot! Leave a little surprise for friends or family. Tap the "Bury" button to start.`
                    : "You’ve discovered an empty spot! Check back soon for nearby time capsules."}
            </Typography>
          )}
          {!isHiddenByFilters && showBuryCta && !isInsidersView && (
            <Button
              fullWidth
              startIcon={
                <Box
                  component="img"
                  src={IconMessageWhite}
                  alt="bury icon"
                  sx={{ width: 24, height: 24 }}
                />
              }
              onClick={() => {
                if (onBury) onBury();
                onClose();
              }}
              variant="contained"
              sx={(theme) => ({
                backgroundColor: theme.colors.blue,
                color: theme.palette.primary.white,
                p: "10px 20px",
                textTransform: "none",
                borderRadius: "50px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              })}
            >
              Bury
            </Button>
          )}
          </Box>
        ) : (
          <>
            {!isInsidersView && String(answerText || "").trim() ? (
              <Box
                sx={(theme) => ({
                  mb: 1.25,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1,
                  color: theme.palette.text.secondary,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.05)
                      : alpha(theme.palette.common.black, 0.04),
                })}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                  {String(answerText || "").trim()}
                </Typography>
              </Box>
            ) : null}
            {renderList()}
          </>
        )}
      </Box>

      {!suppressCategoryControls && !isLocationClusterMode && viewMode === "discover" && onRefreshUncover ? (
        <Button
          variant="text"
          onClick={onRefreshUncover}
          sx={{ mt: 1, textTransform: "none", fontWeight: 700, alignSelf: "center", flexShrink: 0 }}
        >
          Refresh uncover
        </Button>
      ) : null}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
          // Keep the same 12px breathing room vertically as we do horizontally.
          paddingTop: `calc(${TOP_APP_BAR_HEIGHT}px + 12px)`,
          paddingBottom: `calc(${BOTTOM_NAV_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 12px + ${bottomReservedSpace})`,
          transition: "padding-bottom 260ms ease",
        },
        "& .MuiPaper-root": {
          margin: "0 12px",
          width: "min(920px, calc(100vw - 24px))",
          maxHeight: `calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - ${TOP_APP_BAR_HEIGHT + BOTTOM_NAV_BAR_HEIGHT + 24}px - ${bottomReservedSpace})`,
          display: "flex",
          flexDirection: "column",
          transition: "max-height 260ms ease",
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: "center", m: 0, pt: 1, fontSize: "1rem", position: "relative", pr: 5 }}
      >
        {viewMode === "drafts"
          ? "Continue Drafts"
          : viewMode === "insiders"
            ? "Insider Gems"
          : isLocationClusterMode
            ? `${orderedFilteredMarkers.length} Time Capsules Here`
            : isFindResultsView
              ? findLocationLabel
                ? `Nearby Finds in ${findLocationLabel}`
                : "Nearby Finds"
            : isDiscoverResultsView
              ? discoverResultsTitle
              : "Uncover Nearby Time Capsules"}
        <IconButton
          aria-label="Close uncover list"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        ref={contentRef}
        onScroll={handleContentScroll}
        sx={{ flex: 1, overflow: "hidden", pt: 2, pb: 1.5, px: 2, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {viewMode === "drafts" ? renderDraftList() : renderDiscoverView()}
      </DialogContent>
    </Dialog>
  );
}
